import { canDo, PermissionContext } from '@ardoq/access-control';
import { APIEntityType, APIFieldType, ArdoqId } from '@ardoq/api-types';
import { EnhancedScopeData, ScenarioModeState } from '@ardoq/data-model';
import { getEntityById } from '@ardoq/renderers';
import { documentArchiveInterface } from 'modelInterface/documentArchiveInterface';
import { type ComponentStyleImageOption } from 'appModelStateEdit/propertiesEditor/componentStylePropertiesEditor/utils';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';
import { logError } from '@ardoq/logging';
import { derivedStream } from '@ardoq/rxbeach';
import { fields$, type FieldsState } from 'streams/fields/fields$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { appModelStateEdit$ } from 'appModelStateEdit/appModelStateEdit$';
import { PropertiesEditorStreamShape } from 'appModelStateEdit/propertiesEditor/types';
import {
  canDelete,
  canShowTags,
  getHeaderTitle,
  getLockedMessage,
  getMultiEditingMessage,
  getPrimaryButtonLabel,
  getPropertyGroups,
  getSaveButtonDisabledState,
  getSidebarInstanceId,
  getTags,
  isAnyEntityLocked,
  isEntityCalculatedField,
  isEntityCalculatedFieldWithoutQuery,
  isEntityDateRangeField,
} from 'appModelStateEdit/propertiesEditor/utils';
import { getEditableEntityPropertiesToCompare } from 'appModelStateEdit/propertiesEditor/editableEntityProperties';
import { AppModelStateEditStreamShape } from 'appModelStateEdit/types';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { debounceTime, map } from 'rxjs/operators';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { constraintsEnforcementMode$ } from 'streams/constraintsEnforcementMode$';
import { context$ } from 'streams/context/context$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { IntendedRefTypes } from '../../utils/metamodelConstraints';
import { intendedRefTypes$ } from './intendedRefTypes$';
import { getReferenceErrorMessage } from './referencePropertiesEditor/utils';

const getComponentStyleImageOptions = (
  componentId: ArdoqId,
  enhancedScopeData: EnhancedScopeData
): ComponentStyleImageOption[] => {
  const component = getEntityById(
    APIEntityType.COMPONENT,
    componentId,
    enhancedScopeData
  );
  if (!component) {
    return [];
  }
  return documentArchiveInterface
    .getDocumentArchiveImages({ wsId: component.rootWorkspace })
    .map(({ filename, uri }) => ({ label: filename, value: uri }));
};

const toNumberFormatByFieldName = (
  fieldsState: FieldsState
): Record<string, string | null> =>
  Object.fromEntries(
    Array.from(fieldsState.byName.values())
      .filter(field => field.type === APIFieldType.NUMBER)
      .map(field => [field.name, field.numberFormatOptions ?? null])
  );

/**
 * Create viewModel$ for the properties editor.
 * NOTE: This is not pure due to all the utils that are used
 */
export const toPropertiesEditorViewModel = (data: {
  appModelStateEdit: AppModelStateEditStreamShape;
  permissionContext: PermissionContext;
  activeScenarioState: ScenarioModeState;
  intendedRefTypes?: IntendedRefTypes;
  isEnforcementEnabled?: boolean;
  locale: Locale;
  i18nLocale: I18nLocale;
  workspaceId?: string;
  numberFormatByFieldName: Record<string, string | null>;
}): PropertiesEditorStreamShape => {
  const {
    appModelStateEdit,
    permissionContext,
    activeScenarioState,
    locale,
    i18nLocale,
    workspaceId,
    numberFormatByFieldName,
  } = data;
  const {
    newEntityAttributes,
    entityIDs,
    entityType,
    model,
    isEditingComponentStyle,
    enhancedScopeData,
    originalEnhancedScopeData,
    dirtyAttributes,
    errorMessage,
    refTypesSortedByMostUsed,
  } = appModelStateEdit;
  if (
    !entityType ||
    !entityIDs?.length ||
    !enhancedScopeData ||
    !originalEnhancedScopeData
  ) {
    return getEmptyPropertiesEditorViewModel(errorMessage, i18nLocale);
  }
  const sidebarInstanceId = getSidebarInstanceId(
    entityType,
    entityIDs,
    enhancedScopeData,
    model
  );

  const isCreatingNewEntity = Boolean(newEntityAttributes);

  const imageOptions =
    isEditingComponentStyle && entityType === APIEntityType.COMPONENT
      ? getComponentStyleImageOptions(entityIDs[0], enhancedScopeData)
      : [];

  const propertyGroups = getPropertyGroups(
    entityType,
    entityIDs,
    activeScenarioState,
    enhancedScopeData,
    dirtyAttributes,
    isCreatingNewEntity,
    permissionContext,
    locale,
    i18nLocale,
    numberFormatByFieldName,
    imageOptions,
    model,
    isEditingComponentStyle,
    refTypesSortedByMostUsed,
    data.intendedRefTypes,
    data.isEnforcementEnabled
  );
  const tags = getTags(
    entityType,
    entityIDs,
    enhancedScopeData,
    permissionContext,
    activeScenarioState,
    isCreatingNewEntity
  );
  const headerTitle = getHeaderTitle(
    entityType,
    isCreatingNewEntity,
    i18nLocale,
    isEditingComponentStyle
  );
  const isCalculatedField = isEntityCalculatedField(
    entityType,
    entityIDs[0],
    enhancedScopeData
  );
  const isCalculatedFieldWithoutQuery = isEntityCalculatedFieldWithoutQuery(
    entityType,
    entityIDs[0],
    enhancedScopeData
  );
  const isDateRangeField = isEntityDateRangeField(
    entityType,
    entityIDs[0],
    enhancedScopeData
  );
  const buttonLabel = getPrimaryButtonLabel(
    isCreatingNewEntity,
    isCalculatedField,
    isCalculatedFieldWithoutQuery,
    i18nLocale
  );
  const isLocked = isAnyEntityLocked(entityType, entityIDs, enhancedScopeData);
  const lockedMessage = getLockedMessage(isLocked, i18nLocale);
  const multiEditingMessage = getMultiEditingMessage(
    entityType,
    entityIDs,
    enhancedScopeData,
    i18nLocale,
    isEditingComponentStyle
  );
  const shouldShowTags = canShowTags(entityType, isEditingComponentStyle);
  const shouldShowSaveButton = !isLocked;

  // For the UI, we always use the first entity's properties for compatibility
  // But for save button logic, we need to check all entities in bulk editing
  const currentEditableProperties = getEditableEntityPropertiesToCompare(
    enhancedScopeData,
    entityType,
    entityIDs[0]
  );
  const originalProperties = getEditableEntityPropertiesToCompare(
    originalEnhancedScopeData,
    entityType,
    entityIDs[0]
  );

  // For bulk editing save button logic, collect all entity properties
  const isMultiEditing = entityIDs.length > 1;
  const allCurrentProperties = isMultiEditing
    ? entityIDs.map(entityId =>
        getEditableEntityPropertiesToCompare(
          enhancedScopeData,
          entityType,
          entityId
        )
      )
    : [currentEditableProperties];
  const allOriginalProperties = isMultiEditing
    ? entityIDs.map(entityId =>
        getEditableEntityPropertiesToCompare(
          originalEnhancedScopeData,
          entityType,
          entityId
        )
      )
    : [originalProperties];

  const isSaveButtonDisabled = getSaveButtonDisabledState(
    propertyGroups,
    isCreatingNewEntity,
    allCurrentProperties,
    allOriginalProperties,
    isMultiEditing
  );
  const shouldShowDeleteButton = canDelete(
    entityType,
    isCreatingNewEntity,
    isLocked,
    isEditingComponentStyle
  );
  const shouldShowCalculatedFieldSettings =
    isCalculatedField &&
    !isCreatingNewEntity &&
    !isCalculatedFieldWithoutQuery &&
    // Necessary due to hack calculated date range field, there is no calculated date range types
    // See: https://help.ardoq.com/en/articles/43910-calculated-date-ranges
    !isDateRangeField;
  const shouldShowComponentStylePreview = Boolean(isEditingComponentStyle);

  const canManageMetamodelConstraints = canDo({
    permissionContext,
    action: 'organization/manage_metamodel_constraints',
    resourceId: 'organization',
  });

  return {
    propertyGroups,
    tags,
    headerTitle,
    buttonLabel,
    shouldShowTags,
    shouldShowSaveButton,
    isSaveButtonDisabled,
    shouldShowDeleteButton,
    shouldShowCalculatedFieldSettings,
    shouldShowComponentStylePreview,
    lockedMessage,
    multiEditingMessage,
    errorMessage:
      errorMessage ??
      getReferenceErrorMessage(
        entityType,
        entityIDs,
        data.intendedRefTypes,
        enhancedScopeData,
        i18nLocale
      ),
    sidebarInstanceId,
    originalProperties,
    canManageMetamodelConstraints,
    i18nLocale,
    workspaceId,
  };
};

const getEmptyPropertiesEditorViewModel = (
  errorMessage: string | null,
  i18nLocale: I18nLocale
) => ({
  propertyGroups: [],
  tags: { value: [], options: [] },
  headerTitle: t(i18nLocale, 'propertiesEditor:header.editing', 'Editing'),
  buttonLabel: t(i18nLocale, 'propertiesEditor:buttons.save', 'Save'),
  shouldShowTags: false,
  shouldShowSaveButton: true,
  isSaveButtonDisabled: false,
  shouldShowDeleteButton: true,
  shouldShowAddField: false,
  addFieldAdminMessage: null,
  shouldShowCalculatedFieldSettings: false,
  shouldShowComponentStylePreview: false,
  shouldShowReferenceTypePreview: false,
  lockedMessage: '',
  multiEditingMessage: '',
  errorMessage,
  sidebarInstanceId: null,
  originalProperties: undefined,
  canManageMetamodelConstraints: false,
  i18nLocale,
});

const propertiesEditor$ = derivedStream(
  'propertiesEditor$',
  appModelStateEdit$,
  currentUserPermissionContext$,
  activeScenario$,
  intendedRefTypes$,
  constraintsEnforcementMode$,
  locale$,
  i18nLocale$,
  context$,
  fields$
).pipe(
  debounceTime(DEFAULT_DEBOUNCE_TIME),

  map(
    ([
      appModelStateEdit,
      permissionContext,
      activeScenarioState,
      intendedRefTypes,
      constraintsEnforcementMode,
      locale,
      i18nLocale,
      context,
      fieldsState,
    ]) => {
      try {
        return toPropertiesEditorViewModel({
          appModelStateEdit,
          permissionContext,
          activeScenarioState,
          intendedRefTypes: intendedRefTypes ?? undefined,
          isEnforcementEnabled: constraintsEnforcementMode === 'strict',
          locale,
          i18nLocale,
          workspaceId: context.workspaceId || undefined,
          numberFormatByFieldName: toNumberFormatByFieldName(fieldsState),
        });
      } catch (e) {
        logError(
          Error('Failed to create properties editor view model', { cause: e })
        );
        return getEmptyPropertiesEditorViewModel(
          t(
            i18nLocale,
            'propertiesEditor:errors.failedToEdit',
            'Failed to edit properties'
          ),
          i18nLocale
        );
      }
    }
  )
);

export { propertiesEditor$ };
